/**
 * The Rückfrage PARAGRAPH one statement contributes to the supplier mail
 * (review-mask prototype, round 4). ONE paragraph, not a whole mail: whichever
 * surface renders it (the card's own collector prefill, the request dialog's
 * fallback body) adds the greeting/intro once per mail and the backend
 * appends the org's signature on send, so a card only ever writes its own
 * question. Built purely from the card's own facts (Geltung,
 * verdict, scope, reference list, framework), never from its evidence
 * quotes, since a statement can carry more than one.
 *
 * Scope is "unclear" only when the Geltung is explicitly "Rückfrage" (`ask`);
 * "Alle Artikel" / "Artikelliste" / "Produktgruppe" all count as resolved,
 * whatever their article count. The verdict is "unclear" only for the
 * extractor's own `unknown`: a null verdict ("noch offen") is a different
 * open point (the reviewer simply has not decided yet) and asks nothing of
 * the supplier. Three cases follow: scope unclear alone, scope and verdict
 * both unclear, verdict unclear alone (scope resolved); a missing reference
 * list adds one more ask on top of whichever of those needs it. An unclear
 * scope whose document terms matched no article quotes those terms instead,
 * and a supplier with no articles in master data never reads that its
 * document names none (TUR-1652).
 *
 * MAPPED, NOT COPIED, from the prototype's `draftFor`: its REACH-only clause
 * ("Kandidatenliste über 0,1 % (w/w)") is framework-neutral here (the mask
 * spans RoHS, PFAS, POP, …), the demo supplier's name is gone from the copy,
 * and "(siehe Anhang)" is not promised: a review-mask Rückfrage is sent
 * WITHOUT an attachment (`attachment_format=NONE`, product decision
 * 2026-09-14), so the reviewer's own mapping is named inline instead.
 */
import type { TranslationKeys } from "@/hooks/useTranslation/useTranslation"
import type { FrameworkEditionInfo } from "../mapping/editionInfo"
import type { Geltung } from "../mapping/geltungMapping"
import type { StatementCard } from "../useStudioV2State"

export type Translate = (
  key: TranslationKeys,
  values?: Record<string, string | number>,
) => string

/** Whether the card asks the supplier anything at all: the ONE predicate
 *  `ArticleRow` mounts the composer on, and what the draft below branches on. */
export function inquiryNeeded(card: StatementCard, geltung: Geltung): boolean {
  return geltung === "ask" || card.verdict === "unknown"
}

/** What the draft depends on: Geltung and scope decide WHETHER scope is an
 *  open question and how to phrase it if not, verdict and reference list
 *  decide the rest. Unchanged since the text was last (re)drafted, an edit
 *  (even to empty) is left alone; changed, the draft is regenerated. */
export function draftFingerprint(
  card: StatementCard,
  geltung: Geltung,
): string {
  return JSON.stringify([
    geltung,
    card.verdict,
    card.scopeAll,
    card.articleNumbers,
    card.listVersion,
    card.scopeReasons ?? [],
    card.unmatchedTerms ?? [],
    card.supplierHasArticles ?? null,
  ])
}

/** The scope reasons under which the statement's own terms are known to have
 *  matched no article (backend `mc_declaration_scope_state`). */
const UNMATCHED_REASONS = new Set(["family_unmatched", "scope_empty"])

/** How many of the document's terms the paragraph quotes before it only
 *  counts the rest, how long one quoted term may be, and the budget of the
 *  whole quoted list. The terms come from an untrusted document and the saved
 *  question is capped server-side (`DeclarationQuestionIn.question`, 2000
 *  characters): together these keep the paragraph well inside that. */
const MAX_QUOTED_TERMS = 20
const MAX_TERM_LENGTH = 60
const MAX_TERMS_LENGTH = 800

/** One document term made safe to quote: control characters and line breaks
 *  become spaces (a PDF fragment must not add line structure to the
 *  question), whitespace collapses, and a term longer than MAX_TERM_LENGTH
 *  is cut with a trailing "...", counted in code points. */
function cleanTerm(term: string): string {
  const flat = term
    .replace(/\p{Cc}/gu, " ")
    .replace(/\s+/g, " ")
    .trim()
  const chars = Array.from(flat)
  return chars.length > MAX_TERM_LENGTH
    ? `${chars.slice(0, MAX_TERM_LENGTH - 3).join("")}...`
    : flat
}

/** The document's own article references that matched nothing, cleaned for
 *  quoting, or none when no unmatched reason stands for this statement
 *  (TUR-1652). */
function unmatchedTerms(card: StatementCard): string[] {
  const reasons = card.scopeReasons ?? []
  if (!reasons.some((r) => UNMATCHED_REASONS.has(r))) return []
  return (card.unmatchedTerms ?? []).map(cleanTerm).filter(Boolean)
}

/** At most MAX_QUOTED_TERMS terms, and only while the joined list stays
 *  within MAX_TERMS_LENGTH; every term left out is counted in the "and N
 *  more" suffix. */
function quotedTerms(terms: string[], translate: Translate): string {
  const quoted: string[] = []
  let length = 0
  for (const term of terms.slice(0, MAX_QUOTED_TERMS)) {
    const next = length + (quoted.length > 0 ? 2 : 0) + term.length
    if (next > MAX_TERMS_LENGTH) break
    quoted.push(term)
    length = next
  }
  const list = quoted.join(", ")
  const rest = terms.length - quoted.length
  return rest > 0
    ? `${list}${translate("MC_DECL_V2_ASKC_DRAFT_TERMS_MORE", { n: rest })}`
    : list
}

/** Scope unclear, no document terms to quote: the "cannot assign" sentence,
 *  with the reviewer's own (partial) mapping named inline, never "(siehe
 *  Anhang)": this mail carries no attachment (module doc). */
function scopeSentence(
  card: StatementCard,
  frameworkName: string,
  translate: Translate,
): string {
  const confirm =
    card.articleNumbers.length > 0
      ? translate("MC_DECL_V2_ASKC_DRAFT_SCOPE_CONFIRM", {
          candidates: card.articleNumbers.join(", "),
        })
      : ""
  return translate("MC_DECL_V2_ASKC_DRAFT_SCOPE", {
    framework: frameworkName,
    confirm,
  })
}

function referenceListName(
  card: StatementCard,
  editionInfo: FrameworkEditionInfo | null,
): string | null {
  return (
    editionInfo?.options.find((o) => o.id === card.listVersion)?.name ?? null
  )
}

/** How a resolved scope reads in the sentence: each case is its own whole
 *  sentence key rather than a verb placeholder, so every language conjugates
 *  ("enthält"/"enthalten", "contains"/"contain") in its own catalog. */
function verdictKey(card: StatementCard, geltung: Geltung): TranslationKeys {
  if (card.scopeAll) return "MC_DECL_V2_ASKC_DRAFT_VERDICT_ALL"
  if (geltung === "group" || geltung === "material")
    return "MC_DECL_V2_ASKC_DRAFT_VERDICT_GROUP"
  if (card.articleNumbers.length === 0)
    return "MC_DECL_V2_ASKC_DRAFT_VERDICT_NONE"
  if (card.articleNumbers.length === 1)
    return "MC_DECL_V2_ASKC_DRAFT_VERDICT_ONE"
  return "MC_DECL_V2_ASKC_DRAFT_VERDICT_MANY"
}

/**
 * Hydrates the paragraph from what's actually missing. Returns "" for a
 * card that asks nothing (`inquiryNeeded` false); callers mount the
 * composer only when it does, so this is a type-level fallback, not a
 * reachable state.
 */
export function draftFor(
  card: StatementCard,
  geltung: Geltung,
  frameworkName: string,
  editionInfo: FrameworkEditionInfo | null,
  translate: Translate,
): string {
  const scopeUnclear = geltung === "ask"
  const verdictUnclear = card.verdict === "unknown"
  if (!scopeUnclear && !verdictUnclear) return ""

  const listName = referenceListName(card, editionInfo)
  const list = listName
    ? translate("MC_DECL_V2_ASKC_LIST_NAMED", { name: listName })
    : translate("MC_DECL_V2_ASKC_LIST_CURRENT")
  const withListAsk = (sentence: string) =>
    listName ? sentence : `${sentence} ${translate("MC_DECL_V2_ASKC_LIST_ASK")}`

  // TUR-1652: the combined sentence below tells the supplier the document
  // names no articles. That is false when it named some that matched nothing
  // (quote them back instead), and unprovable when the supplier has no
  // articles in master data at all (say only that we cannot assign it).
  const terms = unmatchedTerms(card)
  if (
    scopeUnclear &&
    (terms.length > 0 || card.supplierHasArticles === false)
  ) {
    const scope =
      terms.length > 0
        ? translate("MC_DECL_V2_ASKC_DRAFT_TERMS", {
            framework: frameworkName,
            terms: quotedTerms(terms, translate),
          })
        : scopeSentence(card, frameworkName, translate)
    if (!verdictUnclear) return scope
    return withListAsk(
      `${scope} ${translate("MC_DECL_V2_ASKC_DRAFT_VERDICT_ASK", { list })}`,
    )
  }
  if (scopeUnclear && verdictUnclear) {
    return withListAsk(
      translate("MC_DECL_V2_ASKC_DRAFT_BOTH", {
        framework: frameworkName,
        list,
      }),
    )
  }
  if (scopeUnclear) return scopeSentence(card, frameworkName, translate)
  const key = verdictKey(card, geltung)
  const values: Record<string, string | number> = {
    framework: frameworkName,
    list,
  }
  if (key === "MC_DECL_V2_ASKC_DRAFT_VERDICT_ONE")
    values.article = card.articleNumbers[0]
  if (key === "MC_DECL_V2_ASKC_DRAFT_VERDICT_MANY")
    values.n = card.articleNumbers.length
  return withListAsk(translate(key, values))
}
